import type { IAuditLogProperties } from "../dto";
import type {
  ProcessStatusEnum,
  ProvideLogActionEnum,
  RideStatusEnum,
  RydeProcessTitle,
} from "../enums";
import type { RideInvestigationSeverityEnum } from "./ride-investigation.models";

/**
 * Ride Investigation History — client contract, mirroring the backend RideInvestigationHistoryDto
 * (GET /api/v1/core/c/{companyId}/rides/{rideId}/investigation/history).
 *
 * Everything that happened to the ride outside the drive itself: creation, audited changes, bulk
 * processes, supplier calls and passenger add/remove, oldest first. The drive is condensed on the client
 * from the investigation payload it already holds.
 */

export enum RideInvestigationHistorySourceEnum {
  Lifecycle = "Lifecycle",
  Audit = "Audit",
  Process = "Process",
  Supplier = "Supplier",
  Passenger = "Passenger",
}

export enum RideInvestigationHistoryEntryTypeEnum {
  RideCreated = "RideCreated",
  RecordCreated = "RecordCreated",
  RecordUpdated = "RecordUpdated",
  RecordDeleted = "RecordDeleted",
  StatusChanged = "StatusChanged",
  BulkAction = "BulkAction",
  SupplierCreate = "SupplierCreate",
  SupplierUpdate = "SupplierUpdate",
  SupplierCancel = "SupplierCancel",
  SupplierPolling = "SupplierPolling",
  SupplierAuthorize = "SupplierAuthorize",
  SupplierCall = "SupplierCall",
  SupplierFailed = "SupplierFailed",
  PassengerAdded = "PassengerAdded",
  PassengerRemoved = "PassengerRemoved",
}

export enum RideInvestigationHistoryOriginEnum {
  Manual = "Manual",
  Production = "Production",
  Course = "Course",
  Unknown = "Unknown",
}

export interface IRideInvestigationHistoryCreationDto {
  origin: RideInvestigationHistoryOriginEnum;
  productionId?: number;
  courseId?: number;
  journeyId?: number;
  journeyLegNumber?: number;
  journeyLegsCount?: number;
}

export interface IRideInvestigationHistoryAuditDto {
  eventName: string;
  changedFields: string[];
  status?: RideStatusEnum;
  processId?: number;
  /** The audit row's id — rows are replayed in this order to show what each change did. */
  logId: number;
  /** The row's diff format (metadata.diffVersion), which decides how its arrays are replayed. */
  diffVersion?: number;
  /** What the change wrote, as the ride's history drawer gets it (field-level permissions already applied). */
  properties?: IAuditLogProperties;
}

export interface IRideInvestigationHistoryProcessDto {
  id: number;
  title: RydeProcessTitle;
  status: ProcessStatusEnum;
  total?: number;
  failedCount?: number;
  hasFailedForRide: boolean;
}

export interface IRideInvestigationHistorySupplierDto {
  action: ProvideLogActionEnum;
  provider: string;
  flowAction?: string;
  httpStatus?: number;
  /** Present only when the caller may read provider logs. */
  requestPayload?: string;
  responsePayload?: string;
}

export interface IRideInvestigationHistoryPassengerDto {
  action: string;
  passengerId: number;
  passengerName?: string;
}

export interface IRideInvestigationHistoryEntryDto {
  id: string;
  source: RideInvestigationHistorySourceEnum;
  type: RideInvestigationHistoryEntryTypeEnum;
  severity: RideInvestigationSeverityEnum;
  timestamp: TDateISO;
  endTimestamp?: TDateISO;
  labelKey: string;
  actorName?: string;
  /** Free text written by the code that made the change — not translated. */
  detail?: string;
  creation?: IRideInvestigationHistoryCreationDto;
  audit?: IRideInvestigationHistoryAuditDto;
  process?: IRideInvestigationHistoryProcessDto;
  supplier?: IRideInvestigationHistorySupplierDto;
  passenger?: IRideInvestigationHistoryPassengerDto;
}

export interface IRideInvestigationHistoryDto {
  rideId: number;
  entries: IRideInvestigationHistoryEntryDto[];
  /** Sources that hit their row cap, so their oldest rows are missing. */
  limitedSources: RideInvestigationHistorySourceEnum[];
  /** Sources that failed to load; the rest is still returned. */
  unavailableSources: RideInvestigationHistorySourceEnum[];
  canViewSupplierPayloads: boolean;
}
